import React, { createContext, useContext, useEffect, useState } from 'react';
import { notification, Badge } from 'antd';
import { MessageOutlined, BellOutlined } from '@ant-design/icons';
import { AppContext } from '../context/AppContext';
import discussionService from '../utils/discussionService';

const DiscussionNotificationContext = createContext();

export const useDiscussionNotifications = () => {
  const context = useContext(DiscussionNotificationContext);
  if (!context) {
    throw new Error('useDiscussionNotifications must be used within DiscussionNotificationProvider');
  }
  return context;
};

export const DiscussionNotificationProvider = ({ children }) => {
  const { user, company } = useContext(AppContext);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (user && company && token) {
      initializeNotifications();
      return () => {
        discussionService.disconnect();
      };
    }
  }, [user, company, token]);

  const initializeNotifications = async () => {
    try {
      // Request notification permission
      await discussionService.requestNotificationPermission();
      
      // Connect to WebSocket
      discussionService.connectWebSocket(user.id, company.id, token);
      
      // Set up event listeners
      setupEventListeners();
      
      // Update user presence
      await discussionService.updateUserPresence(
        user.id,
        company.id,
        'online',
        null,
        token
      );

      setIsConnected(true);
    } catch (error) {
      console.error('Failed to initialize notifications:', error);
    }
  };

  const setupEventListeners = () => {
    // New message notifications
    discussionService.addEventListener('new_message', (data) => {
      const { message, topic_id } = data;
      
      // Don't notify if it's the current user's message
      if (message.user_id === user.id) return;
      
      // Show browser notification
      discussionService.showNotification(
        'New Discussion Message',
        `${message.user.first_name} ${message.user.last_name}: ${message.message.substring(0, 50)}...`,
        null,
        () => {
          // Navigate to the discussion forum
          window.location.href = `/discussion-forum?topic=${topic_id}`;
        }
      );

      // Show in-app notification
      notification.info({
        message: 'New Discussion Message',
        description: `${message.user.first_name} ${message.user.last_name} posted in discussion`,
        icon: <MessageOutlined style={{ color: '#1890ff' }} />,
        placement: 'topRight',
        duration: 4,
        onClick: () => {
          window.location.href = `/discussion-forum?topic=${topic_id}`;
        }
      });

      // Update unread count
      setUnreadCount(prev => prev + 1);
      
      // Add to notifications list
      setNotifications(prev => [{
        id: Date.now(),
        type: 'new_message',
        title: 'New Discussion Message',
        message: `${message.user.first_name} ${message.user.last_name} posted in discussion`,
        timestamp: new Date(),
        topic_id: topic_id,
        read: false
      }, ...prev.slice(0, 49)]); // Keep last 50 notifications
    });

    // Topic creation notifications
    discussionService.addEventListener('new_topic', (data) => {
      const { topic } = data;
      
      if (topic.created_by.id === user.id) return;
      
      notification.info({
        message: 'New Discussion Topic',
        description: `${topic.created_by.first_name} ${topic.created_by.last_name} created: ${topic.title}`,
        icon: <MessageOutlined style={{ color: '#52c41a' }} />,
        placement: 'topRight',
        duration: 4,
        onClick: () => {
          window.location.href = `/discussion-forum?topic=${topic.id}`;
        }
      });
    });

    // User presence updates
    discussionService.addEventListener('presence_update', (data) => {
      const { user_id, status, current_topic } = data;
      
      // Handle presence updates (could be used for showing who's online)
      console.log(`User ${user_id} is now ${status}`, { current_topic });
    });

    // Typing indicators
    discussionService.addEventListener('typing', (data) => {
      const { topic_id, user_id, is_typing } = data;
      
      // Handle typing indicators
      console.log(`User ${user_id} is ${is_typing ? 'typing' : 'not typing'} in topic ${topic_id}`);
    });

    // Reaction notifications
    discussionService.addEventListener('reaction_added', (data) => {
      const { message_id, user_id, reaction } = data;
      
      // Handle reaction notifications
      console.log(`User ${user_id} added ${reaction} to message ${message_id}`);
    });

    // Connection status
    discussionService.addEventListener('connection_status', (data) => {
      setIsConnected(data.connected);
    });
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
    
    // Update unread count
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
    setUnreadCount(0);
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const value = {
    unreadCount,
    isConnected,
    notifications,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    discussionService
  };

  return (
    <DiscussionNotificationContext.Provider value={value}>
      {children}
    </DiscussionNotificationContext.Provider>
  );
};

export default DiscussionNotificationProvider;